/**
 * ManageEmailsPage · v2.7.65+E138
 * ══════════════════════════════════════════════════════════════════════
 * General-Partner-only Corporate Email > Manage Emails hub. Lets a GP
 * provision new mailboxes (shared corporate or individual), assign or
 * unassign them across Admin Console users, and audit the deduplication
 * ledger that prevents inbound-email credit drain.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Loader2, Mail, Plus, Users, Trash2, Shield, X, RefreshCw,
  UserPlus, AtSign, AlertTriangle, Search, CheckCircle2, UsersRound, Crown,
} from "lucide-react";

function TypePill({ type }) {
  const isShared = type === "shared_corporate";
  return (
    <span
      className={`text-[9px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border ${
        isShared
          ? "bg-fuchsia-500/20 border-fuchsia-400/50 text-fuchsia-300"
          : "bg-sky-500/20 border-sky-400/50 text-sky-300"
      }`}
    >
      {isShared ? "SHARED" : "INDIVIDUAL"}
    </span>
  );
}

function CreateMailboxForm({ onCreated }) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [type, setType] = useState("shared_corporate");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Enter an email address"); return; }
    setBusy(true);
    try {
      await adminApi.post("/admin/email/manage/mailboxes", {
        email_address: email.trim().toLowerCase(),
        display_name:  displayName.trim() || null,
        type,
        assigned_user_ids: [],
      });
      toast.success(`Mailbox ${email} created`);
      setEmail(""); setDisplayName("");
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create mailbox");
    } finally { setBusy(false); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800" data-testid="mailbox-create-card">
      <CardHeader>
        <CardTitle className="text-zinc-100 flex items-center gap-2">
          <Plus className="h-5 w-5 text-amber-400"/> Create New Mailbox
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="e.g. dispatch@bullertaxi.com"
            type="email"
            required
            className="md:col-span-2 bg-zinc-950 border-zinc-700 text-zinc-100"
            data-testid="mailbox-create-email"
          />
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Display name (optional)"
            className="bg-zinc-950 border-zinc-700 text-zinc-100"
            data-testid="mailbox-create-display-name"
          />
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100"
            data-testid="mailbox-create-type"
          >
            <option value="shared_corporate">Shared Corporate</option>
            <option value="individual">Individual</option>
          </select>
          <div className="md:col-span-4">
            <Button
              type="submit"
              disabled={busy}
              className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
              data-testid="mailbox-create-submit"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : <Plus className="h-4 w-4 mr-1"/>}
              Create Mailbox
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function MailboxRow({ mailbox, directory, onChanged }) {
  const [assignUserId, setAssignUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState("");
  const [picked, setPicked] = useState({});   // { user_id: true }
  const [bulkBusy, setBulkBusy] = useState(false);

  const assign = async () => {
    if (!assignUserId.trim()) { toast.error("Enter a user id or email"); return; }
    setBusy(true);
    try {
      await adminApi.post(`/admin/email/manage/mailboxes/${mailbox.mailbox_id}/assign`, {
        user_id: assignUserId.trim(),
      });
      toast.success(`Assigned to ${assignUserId}`);
      setAssignUserId("");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Assignment failed");
    } finally { setBusy(false); }
  };

  const unassign = async (uid) => {
    try {
      await adminApi.post(`/admin/email/manage/mailboxes/${mailbox.mailbox_id}/unassign`, { user_id: uid });
      toast.success(`Unassigned ${uid}`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unassign failed");
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${mailbox.email_address}? This removes the mailbox from every user it's assigned to.`)) return;
    try {
      await adminApi.delete(`/admin/email/manage/mailboxes/${mailbox.mailbox_id}`);
      toast.success("Mailbox deleted");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const users = mailbox.assigned_user_ids || [];
  const assignedSet = useMemo(() => new Set(users), [users]);

  // v2.7.65+E139 · Filter the directory for the picker. Hide users
  // already assigned so the checklist only shows fresh candidates.
  const pickerCandidates = useMemo(() => {
    const t = pickerQ.trim().toLowerCase();
    return (directory || []).filter((u) => {
      if (assignedSet.has(u.user_id) || assignedSet.has(u.email)) return false;
      if (!t) return true;
      return (
        (u.email || "").toLowerCase().includes(t) ||
        (u.display_name || "").toLowerCase().includes(t) ||
        (u.roles || []).some((r) => r.toLowerCase().includes(t))
      );
    });
  }, [directory, pickerQ, assignedSet]);

  const pickedCount = Object.values(picked).filter(Boolean).length;

  const bulkAssign = async () => {
    const ids = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) { toast.error("Select at least one user"); return; }
    setBulkBusy(true);
    try {
      await adminApi.post(
        `/admin/email/manage/mailboxes/${mailbox.mailbox_id}/assign-bulk`,
        { user_ids: ids },
      );
      toast.success(`Assigned ${ids.length} user${ids.length > 1 ? "s" : ""}`);
      setPicked({});
      setPickerQ("");
      setPickerOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk assign failed");
    } finally { setBulkBusy(false); }
  };

  return (
    <div
      className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 space-y-3"
      data-testid={`mailbox-row-${mailbox.mailbox_id}`}
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <AtSign className="h-4 w-4 text-amber-400"/>
            <span className="font-mono font-bold text-zinc-100">{mailbox.email_address}</span>
            <TypePill type={mailbox.type}/>
          </div>
          {mailbox.display_name && mailbox.display_name !== mailbox.email_address && (
            <div className="text-xs text-zinc-400 mt-1">{mailbox.display_name}</div>
          )}
        </div>
        <Button
          size="sm" variant="ghost"
          onClick={del}
          data-testid={`mailbox-delete-${mailbox.mailbox_id}`}
          className="text-rose-400 hover:text-rose-300"
        >
          <Trash2 className="h-3.5 w-3.5"/>
        </Button>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1.5 flex items-center gap-1">
          <Users className="h-3 w-3"/>Assigned Users ({users.length})
        </div>
        {users.length === 0 ? (
          <div className="text-xs text-zinc-500 italic">No users assigned yet.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {users.map((u) => (
              <span
                key={u}
                className="inline-flex items-center gap-1 text-xs bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-full pl-2 pr-1 py-0.5"
                data-testid={`mailbox-assignee-${mailbox.mailbox_id}-${u}`}
              >
                {u}
                <button onClick={() => unassign(u)} className="hover:bg-rose-500/20 rounded-full p-0.5" title="Unassign">
                  <X className="h-3 w-3"/>
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* v2.7.65+E139 · Bulk-assign picker */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-2.5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1">
            <UsersRound className="h-3 w-3"/>Bulk assign
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPickerOpen((v) => !v)}
            data-testid={`mailbox-bulk-toggle-${mailbox.mailbox_id}`}
            className="h-7 border-zinc-700 text-zinc-200 text-xs"
          >
            {pickerOpen ? "Close picker" : `Pick users (${pickerCandidates.length} available)`}
          </Button>
        </div>
        {pickerOpen && (
          <div className="mt-2 space-y-2" data-testid={`mailbox-bulk-picker-${mailbox.mailbox_id}`}>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500"/>
              <Input
                value={pickerQ}
                onChange={(e) => setPickerQ(e.target.value)}
                placeholder="Filter by name / email / role…"
                className="pl-8 h-8 text-xs bg-zinc-900 border-zinc-700 text-zinc-100"
                data-testid={`mailbox-bulk-search-${mailbox.mailbox_id}`}
              />
            </div>
            <div className="max-h-56 overflow-y-auto rounded border border-zinc-800 divide-y divide-zinc-800">
              {pickerCandidates.length === 0 ? (
                <div className="p-3 text-xs italic text-zinc-500 text-center">
                  No matching users.
                </div>
              ) : pickerCandidates.map((u) => (
                <label
                  key={u.user_id}
                  className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-zinc-900 cursor-pointer text-xs"
                  data-testid={`mailbox-bulk-user-${mailbox.mailbox_id}-${u.user_id}`}
                >
                  <input
                    type="checkbox"
                    checked={!!picked[u.user_id]}
                    onChange={(e) => setPicked((prev) => ({ ...prev, [u.user_id]: e.target.checked }))}
                    className="h-3.5 w-3.5 accent-amber-500"
                    data-testid={`mailbox-bulk-checkbox-${mailbox.mailbox_id}-${u.user_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-zinc-100 truncate">
                      {u.is_general_partner && <Crown className="h-3 w-3 text-amber-300 flex-shrink-0"/>}
                      <span className="truncate font-medium">{u.display_name || u.email || u.user_id}</span>
                    </div>
                    <div className="text-[10px] text-zinc-500 truncate">
                      {u.email}{u.roles?.length ? ` · ${u.roles.join(", ")}` : ""}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              <div className="text-[10px] text-zinc-400">
                <span className="font-bold text-zinc-200">{pickedCount}</span> selected
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm" variant="ghost"
                  onClick={() => setPicked({})}
                  className="h-7 text-xs text-zinc-400"
                  data-testid={`mailbox-bulk-clear-${mailbox.mailbox_id}`}
                >
                  Clear
                </Button>
                <Button
                  size="sm"
                  onClick={bulkAssign}
                  disabled={bulkBusy || pickedCount === 0}
                  className="h-7 text-xs bg-amber-500 hover:bg-amber-400 text-black font-bold"
                  data-testid={`mailbox-bulk-submit-${mailbox.mailbox_id}`}
                >
                  {bulkBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin"/> : <UserPlus className="h-3 w-3 mr-1"/>}
                  Assign {pickedCount || ""}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Input
          value={assignUserId}
          onChange={(e) => setAssignUserId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && assign()}
          placeholder="Or assign one by user id / email…"
          className="flex-1 bg-zinc-950 border-zinc-700 text-zinc-100 h-8 text-sm"
          data-testid={`mailbox-assign-input-${mailbox.mailbox_id}`}
        />
        <Button
          size="sm"
          onClick={assign}
          disabled={busy}
          data-testid={`mailbox-assign-btn-${mailbox.mailbox_id}`}
          className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <UserPlus className="h-3.5 w-3.5"/>}
        </Button>
      </div>
    </div>
  );
}

export default function ManageEmailsPage() {
  const [loading, setLoading] = useState(true);
  const [gp, setGp] = useState(false);
  const [mailboxes, setMailboxes] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [dedupeStats, setDedupeStats] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, stats, dir] = await Promise.all([
        adminApi.get("/admin/email/manage/mailboxes").catch((e) => {
          if (e?.response?.status === 403) { setGp(false); return null; }
          throw e;
        }),
        adminApi.get("/admin/email/manage/dedupe/stats").catch(() => null),
        adminApi.get("/admin/email/manage/users/directory").catch(() => null),
      ]);
      if (list) {
        setMailboxes(list.data.mailboxes || []);
        setGp(true);
      }
      if (stats) setDedupeStats(stats.data);
      if (dir) setDirectory(dir.data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load mailboxes");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = mailboxes.filter((m) => {
    if (!q.trim()) return true;
    const t = q.trim().toLowerCase();
    return (m.email_address || "").toLowerCase().includes(t)
        || (m.display_name || "").toLowerCase().includes(t);
  });

  if (loading) {
    return <div className="min-h-screen bg-zinc-950 text-zinc-100 p-8 flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin mr-2"/>Loading Manage Emails…
    </div>;
  }

  if (!gp) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 p-8 grid place-items-center" data-testid="manage-emails-403">
        <div className="max-w-md rounded-xl border-2 border-rose-500/50 bg-rose-500/10 p-6 text-center">
          <Shield className="h-10 w-10 text-rose-400 mx-auto mb-3"/>
          <h1 className="text-lg font-black text-rose-200 uppercase tracking-widest">General Partners only</h1>
          <p className="text-sm text-zinc-300 mt-2">
            The Manage Emails hub is restricted to General Partners. Ask a GP to assign you the mailboxes you need — you&apos;ll then see them in your inbox picker.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 sm:px-6 lg:px-10 py-8" data-testid="manage-emails-page">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-amber-300 uppercase tracking-tight flex items-center gap-2" data-testid="manage-emails-title">
              <Mail className="h-7 w-7"/>Manage Emails
            </h1>
            <p className="text-sm text-zinc-400 mt-1">
              Provision corporate mailboxes and assign them to Admin Console users. Shared inboxes fire background workers <span className="font-bold text-amber-300">once per message globally</span> so credits stay under control.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={load}
            data-testid="manage-emails-refresh"
            className="border-zinc-700 text-zinc-200"
          >
            <RefreshCw className="h-4 w-4 mr-1"/>Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3 text-center" data-testid="manage-emails-count-total">
            <div className="text-2xl font-black text-zinc-100">{mailboxes.length}</div>
            <div className="text-[10px] uppercase text-zinc-500 tracking-widest font-bold mt-1">TOTAL MAILBOXES</div>
          </div>
          <div className="rounded-lg border border-fuchsia-400/30 bg-fuchsia-500/10 p-3 text-center" data-testid="manage-emails-count-shared">
            <div className="text-2xl font-black text-fuchsia-300">
              {mailboxes.filter((m) => m.type === "shared_corporate").length}
            </div>
            <div className="text-[10px] uppercase text-fuchsia-200/80 tracking-widest font-bold mt-1">SHARED CORPORATE</div>
          </div>
          <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 p-3 text-center" data-testid="manage-emails-count-dedupe">
            <div className="text-2xl font-black text-emerald-300">{dedupeStats?.total_processed ?? 0}</div>
            <div className="text-[10px] uppercase text-emerald-200/80 tracking-widest font-bold mt-1">DEDUPED MESSAGES</div>
          </div>
        </div>

        <CreateMailboxForm onCreated={load}/>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500"/>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search mailboxes…"
            className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-100"
            data-testid="manage-emails-search"
          />
        </div>

        <div className="space-y-3" data-testid="manage-emails-list">
          {filtered.length === 0 ? (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-8 text-center text-zinc-500" data-testid="manage-emails-empty">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-40"/>
              {mailboxes.length === 0 ? "No mailboxes provisioned yet. Create your first one above." : "No mailboxes match your search."}
            </div>
          ) : filtered.map((m) => (
            <MailboxRow key={m.mailbox_id} mailbox={m} directory={directory} onChanged={load}/>
          ))}
        </div>

        <div className="rounded-lg border border-emerald-400/20 bg-emerald-500/5 p-3 flex items-start gap-2 text-xs text-emerald-100/90" data-testid="manage-emails-guardrail-note">
          <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5"/>
          <div>
            <span className="font-bold">Credit Guardrail Active.</span>{" "}
            Inbound messages to any mailbox are checked against the dedupe ledger before an agent runs. Duplicate deliveries (multi-user shared inbox) resolve to a <span className="font-mono">is_first:false</span> response and skip further worker execution.
          </div>
        </div>
      </div>
    </div>
  );
}
